import React from 'react';
import { useQuery } from '@common/hooks';
import { Button } from 'antd';
import {
  Drawer,
  ErrorHandler,
  FileViewer,
  Gutter,
  Icon,
  Link,
} from '@common/components';
import {
  SubmittalPacketChangeRequestDrawerTrigger,
  SubmittalPacketLastUpdatedInfo,
} from '@applicant-flow/components';
import { buildDialogUrl } from '@common/services';
import { SUBMITTAL_PACKET_QUERY } from '@applicant-flow/graphql';

export function SubmittalPacketPreviewerDrawer({
  open,
  onClose,
  submittalPacketId,
  applicationId,
  applicationState,
}: {
  open: boolean;
  onClose: () => void;
  submittalPacketId?: string;
  applicationId?: string;
  applicationState?: string;
}) {
  const { data, error, loading } = useQuery(SUBMITTAL_PACKET_QUERY, {
    skip: !submittalPacketId,
    variables: {
      id: submittalPacketId,
    },
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="SubmittalPacketPreviewerDrawer"
        messageType="contextual"
      />
    );
  }

  const submittalPacket = data?.worksSubmittalPacket;

  const firstDocument = submittalPacket?.files?.[0];

  const source = firstDocument
    ? [
        {
          id: firstDocument.id,
          name: firstDocument.filename,
          url: firstDocument.downloadUrl,
          uploadedAt: firstDocument.createdAt,
        },
      ]
    : [];

  const footer =
    applicationState && applicationId ? (
      <SubmittalPacketChangeRequestDrawerTrigger
        applicationId={applicationId}
        applicationState={applicationState}
      >
        <Button size="large">Change Request</Button>
      </SubmittalPacketChangeRequestDrawerTrigger>
    ) : (
      false
    );

  return (
    <Drawer
      size="lg"
      open={open}
      onClose={onClose}
      loading={loading}
      footer={footer}
      title="Submittal Packet"
      className="document-preview-drawer"
    >
      {applicationId && (
        <Gutter direction="bottom" size="md">
          <SubmittalPacketLastUpdatedInfo applicationId={applicationId} />
        </Gutter>
      )}
      <FileViewer source={source} />
    </Drawer>
  );
}

export function SubmittalPacketPreviewerDrawerTrigger({
  applicationId = '',
  applicationState = '',
  submittalPacketId = '',
}: {
  applicationId?: string;
  applicationState?: string;
  submittalPacketId?: string;
}) {
  const SubmittalPacketPreviewerDrawerDrawerUrl = buildDialogUrl(
    'submittal-packet-previewer-drawer',
    {
      overlap: true,
      props: {
        applicationId,
        applicationState,
        submittalPacketId,
      },
    }
  );

  return (
    <Link to={SubmittalPacketPreviewerDrawerDrawerUrl}>
      <Button>
        <Icon icon="copy" kind="regular" />
        <span style={{ marginLeft: 8 }}>View Submittal Packet</span>
      </Button>
    </Link>
  );
}
